import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ImportMessagesParams,
  ImportMessagesBody,
  ImportMessagesData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface ImportMessagesState
  extends MergeRequestState<ImportMessagesData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.importMessages,
  isFetching: (state: ReduxState) => state.server.importMessages.isFetching,
  data: (state: ReduxState) => state.server.importMessages.data,
  error: (state: ReduxState) => state.server.importMessages.error,
};

const initialState: ImportMessagesState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/importMessages';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      body,
      config,
    }: {
      params: ImportMessagesParams;
      body: ImportMessagesBody;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) => serverApi.importMessages(params, body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerImportMessages {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const importMessages: ServerImportMessages = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
